// src/components/separation/retainerreleasecard.tsx
//
// Focused "Retained Balance" card for the Full & Final settlement screen.
// Shows the employee's outstanding retainer pool and lets HR/Admin RELEASE it
// (pay the pool out in the F&F) or HOLD it (keep it withheld, record the choice).
//
// Release/Hold are recorded against the F&F settlement via the settlement
// action endpoint (RELEASE_RETAINED / HOLD_RETAINED), so they are only enabled
// once a settlement exists. Until then the buttons are disabled with a hint.

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { salaryApi } from '../../api/salary';
import { settlementAction } from '../../api/separation';
import { RetainerInfo, SettlementAction } from '../../types/separation';
import ConfirmDialog, { ConfirmState } from '../ConfirmDialog';

interface RetainerReleaseCardProps {
  employeeId: string;
  // When present, release/hold post against this F&F settlement. Until the
  // settlement is created the actions stay disabled (release happens "in F&F").
  settlementId?: string;
  onChanged?: () => void;
}

const inr = (n: number) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;

const RetainerReleaseCard: React.FC<RetainerReleaseCardProps> = ({
  employeeId,
  settlementId,
  onChanged,
}) => {
  const [info, setInfo] = useState<RetainerInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<null | 'release' | 'hold'>(null);
  const [showLedger, setShowLedger] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const load = useCallback(async () => {
    if (!employeeId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await salaryApi.getRetainer(employeeId);
      setInfo(res.data);
    } catch {
      // Silent fallback to empty state — no "could not load" toast (project convention).
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (hold: boolean) => {
    if (!settlementId) return;
    const outstanding = info?.outstanding || 0;
    setConfirm({
      title: hold
        ? `Hold ${inr(outstanding)} of retained balance?`
        : `Release ${inr(outstanding)} of retained balance in this Full & Final settlement?`,
      description: hold
        ? 'It stays withheld and the decision is recorded on this settlement.'
        : 'This pays out the pool.',
      confirmLabel: hold ? 'Hold' : 'Release',
      tone: 'default',
      onConfirm: async () => {
        setActing(hold ? 'hold' : 'release');
        try {
          const payload: SettlementAction = {
            action: hold ? 'HOLD_RETAINED' : 'RELEASE_RETAINED',
          };
          await settlementAction(settlementId, payload);
          toast.success(hold ? 'Retained balance held' : `Released ${inr(outstanding)} in settlement`);
          await load();
          onChanged?.();
        } catch (err: any) {
          toast.error(err?.message || `Could not ${hold ? 'hold' : 'release'} retained balance`);
        } finally {
          setActing(null);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="border rounded-lg p-4" style={{ marginBottom: '1rem' }}>
        <p className="text-sm text-gray-500">Loading retained balance…</p>
      </div>
    );
  }

  const outstanding = info?.outstanding || 0;
  const cfg = info?.config || null;
  const ledger = info?.ledger || [];
  const hasRetainer = !!cfg || outstanding > 0 || ledger.length > 0;

  if (!hasRetainer) {
    return (
      <div className="border rounded-lg p-4" style={{ marginBottom: '1rem' }}>
        <h4 className="font-semibold text-gray-700 mb-3">Retained Balance</h4>
        <p className="text-sm text-gray-500">No retained balance for this employee.</p>
      </div>
    );
  }

  const released = outstanding <= 0;

  return (
    <div className="border rounded-lg p-4" style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
        <div>
          <h4 className="font-semibold text-gray-700">Retained Balance</h4>
          {cfg && (
            <p className="text-xs text-gray-500" style={{ marginTop: '0.25rem' }}>
              {inr(cfg.amount)}/mo · {cfg.period_months}-month cycle · {cfg.recurring ? 'repeating' : 'one-time'}{' '}
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{cfg.status}</span>
            </p>
          )}
        </div>
        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <p className="text-sm text-gray-500">Outstanding pool</p>
          <p className="text-xl font-bold text-blue-600">{inr(outstanding)}</p>
        </div>
      </div>

      {released ? (
        <p className="text-sm text-green-600" style={{ marginTop: '0.75rem' }}>
          Pool released — nothing outstanding.
        </p>
      ) : (
        <>
          <div className="flex gap-3" style={{ marginTop: '1rem' }}>
            <button
              onClick={() => act(false)}
              disabled={!settlementId || acting !== null}
              className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              style={{ opacity: !settlementId || acting !== null ? 0.5 : 1 }}
            >
              {acting === 'release' ? 'Releasing…' : 'Release in F&F'}
            </button>
            <button
              onClick={() => act(true)}
              disabled={!settlementId || acting !== null}
              className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              style={{ opacity: !settlementId || acting !== null ? 0.5 : 1 }}
            >
              {acting === 'hold' ? 'Holding…' : 'Hold'}
            </button>
          </div>
          {!settlementId && (
            <p className="text-xs text-gray-500" style={{ marginTop: '0.5rem' }}>
              Create the settlement first to release the retained balance in F&F.
            </p>
          )}
        </>
      )}

      {ledger.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <button
            onClick={() => setShowLedger((v) => !v)}
            className="text-xs text-blue-600"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            {showLedger ? 'Hide ledger' : `View ledger (${ledger.length})`}
          </button>
          {showLedger && (
            <div className="overflow-y-auto" style={{ maxHeight: '10rem', marginTop: '0.5rem' }}>
              <table className="text-xs" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="text-gray-500">
                    {['Period', 'Kind', 'Withheld', 'Released', 'Balance'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '0.25rem 0.5rem 0.25rem 0', position: 'sticky', top: 0, zIndex: 1, background: '#fff' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((r, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.25rem 0.5rem 0.25rem 0' }}>
                        {String(r.month).padStart(2, '0')}/{r.year}
                      </td>
                      <td style={{ padding: '0.25rem 0.5rem 0.25rem 0' }}>{r.kind}</td>
                      <td style={{ padding: '0.25rem 0.5rem 0.25rem 0' }}>{inr(r.withheld)}</td>
                      <td style={{ padding: '0.25rem 0.5rem 0.25rem 0' }}>{inr(r.released)}</td>
                      <td style={{ padding: '0.25rem 0.5rem 0.25rem 0' }}>{inr(r.balance_after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
};

export default RetainerReleaseCard;
